import { useState } from 'react';
import { CommonDataColumn, MediaList, OptionType, OptionMore } from 'models/BaseModel';
import { apiClient, apiFileUpload } from 'services/tokenInterceptor';
import { Utility } from 'helpers/UtilityHelpers';
import { Code } from 'constants/codes';


const getDefaultOption = (first: number): OptionType | undefined => {
  const optionMap: Record<number, OptionType> = {
    [Code.Option.Common.NA]: { value: Code.Option.Common.NA, label: "N/A" },
    [Code.Option.Common.SELECT]: { value: Code.Option.Common.SELECT, label: "Select" },
    [Code.Option.Common.ALL]: { value: Code.Option.Common.ALL, label: "All" },
    [Code.Option.Common.NONE]: { value: Code.Option.Common.NONE, label: "None" },
    [Code.Option.Common.GENERAL]: { value: Code.Option.Common.GENERAL, label: "General" },
    [Code.Option.Common.BLANK]: { value: Code.Option.Common.BLANK, label: "" },
  };

  return optionMap[first];
};

const getDefaultMore = (first: number): OptionMore | undefined => {
  const optionMap: Record<number, OptionMore> = {
    [Code.Option.Common.NA]: { value: Code.Option.Common.NA, label: "N/A" },
    [Code.Option.Common.SELECT]: { value: Code.Option.Common.SELECT, label: "Select" },
    [Code.Option.Common.ALL]: { value: Code.Option.Common.ALL, label: "All" },
    [Code.Option.Common.NONE]: { value: Code.Option.Common.NONE, label: "None" },
    [Code.Option.Common.GENERAL]: { value: Code.Option.Common.GENERAL, label: "General" },
    [Code.Option.Common.BLANK]: { value: Code.Option.Common.BLANK, label: "" },
  };

  return optionMap[first];
};

// Function to load JSON from API
const loadFromAPI = async (
  fileName: string,
  first: number,
  setData: (data: OptionType[]) => void,
  setError: (error: string) => void,
  isCommon: boolean = false
) => {

  const url = isCommon
    ? `${Code.Path.SERVER}local/common/${fileName}.json`
    : `${Code.Path.SERVER}local/company/${fileName}.json`;

  try {
    const response = await apiClient.get(url);
    const data: OptionType[] = response.data;

    //console.log("loadFromAPI: Dropdown Data:", data);

    if (!Array.isArray(data)) {
      if ('statusCode' in response.data && response.data.statusCode === 401) {
        window.parent?.parent?.location.replace("/");
        return;
      }
      throw new Error(`Data is not an array`);
    }

    const defaultOption = getDefaultOption(first);
    setData(defaultOption ? [defaultOption, ...data] : data);
  } catch (err) {
    handleError(err, setError);
  }
};

const loadFromAPIMore = async (
  fileName: string,
  first: number,
  setData: (data: OptionMore[]) => void,
  setError: (error: string) => void,
  isCommon: boolean = false
) => {
  const url = isCommon
    ? `${Code.Path.SERVER}local/common/${fileName}.json`
    : `${Code.Path.SERVER}local/company/${fileName}.json`;

  try {
    const response = await apiClient.get(url);
    const data: OptionMore[] = response.data;

    if (!Array.isArray(data)) {
      throw new Error(`Data is not an array`);
    }

    console.log("APIMore:",data);

    const defaultOption = getDefaultMore(first);
    setData(defaultOption ? [defaultOption, ...data] : data);
  } catch (err) {
    setData([]);
    handleError(err, setError);
  }
};

const listFromAPI = async (
  fileName: string,
  setData: (data: string) => void,
  setError: (error: string) => void,
  isCommon: boolean = false,
  first?: string,
  field?: string,
  value?: string,
) => {
  const url = isCommon
    ? `${Code.Path.SERVER}local/common/${fileName}.json`
    : `${Code.Path.SERVER}local/company/${fileName}.json`;

  try {
    const response = await apiClient.get(url);
    const data: OptionType[] = response.data;

    if (!Array.isArray(data)) {
      throw new Error(`Data is not an array`);
    }

    // Filter only if field and value are provided
    const filteredData = field && value
      ? data.filter(item => String((item as any)[field]) === value)
      : data;

    // Create semicolon-separated label string
    const labelList = filteredData.map(item => item.label).join(';');

    // Send result back via setData
    const finalData = first ? `${first};${labelList}` : labelList;
    setData(finalData);
  } catch (err) {
    handleError(err, setError);
  }
};

const loadFromAPISetLocalStorage = async (
  groupNos: string[], // fileNames is an array
  isCommon: boolean = false
) => {
  
    let optionValues = '';
    for (const groupNo of groupNos) {
      try {
        const url = isCommon
          ? `${Code.Path.SERVER}local/common/option${groupNo}.json`
          : `${Code.Path.SERVER}local/company/option${groupNo}.json`;

        const response = await apiClient.get(url);
        const data: OptionType[] = response.data;

        if (!Array.isArray(data)) {
          throw new Error(`Data from ${groupNo} is not an array`);
        }

        const getValuesOnly = data
          .map((item) => item.label)
          .filter((label) => typeof label === "string")
          .join(";");

        optionValues += `${groupNo}::${getValuesOnly};;`;
      } catch (err) {
        console.error(`Error loading files:`, err);
      }
    }
    Utility.Storage.SetSecure(Code.LocalKey.OptionValues, optionValues);
    console.log(`Option Values Saved to local storage:`,optionValues);
};


const loadAnyData = async <T>(
  fileName: string,
  setData: (data: T[]) => void,
  setError: (error: string) => void,
  isCompany: boolean = true
) => {
  const url = isCompany
    ? `${Code.Path.SERVER}local/company/${fileName}.json`
    : `${Code.Path.SERVER}local/common/${fileName}.json`;

  try {
    const response = await apiClient.get<T[]>(url);
    const data = response.data;

    if (!Array.isArray(data)) {
      throw new Error(`Data is not an array`);
    }

    setData(data);

  } catch (err) {
    handleError(err, setError);
  }
};

const fetchAnyData = async <T>(
  fileName: string,
  setError: (error: string) => void,
  isCompany: boolean = true
): Promise<T[] | null> => {
  const url = isCompany
    ? `${Code.Path.SERVER}local/company/${fileName}.json`
    : `${Code.Path.SERVER}local/common/${fileName}.json`;

  try {
    const response = await apiClient.get<T[]>(url);
    const data = response.data;

    if (!Array.isArray(data)) {
      throw new Error(`Data is not an array`);
    }

    return data;
  } catch (err) {
    handleError(err, setError);
    return null;
  }
};

// Function to load JSON via HTTP request
const loadFromHttp = async (
  fileName: string,
  first: number,
  setData: (data: OptionType[]) => void,
  setError: (error: string) => void,
  folder: string = ""
) => {
  const url = folder
    ? `/data/company/${folder}/${fileName}.json`
    : `/data/common/${fileName}.json`;

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to load data: ${response.statusText}`);

    const data: OptionType[] = await response.json();
    if (!Array.isArray(data)) throw new Error(`Data is not an array`);

    const defaultOption = getDefaultOption(first);
    setData(defaultOption ? [defaultOption, ...data] : data);
  } catch (err) {
    handleError(err, setError);
  }
};

const loadFromHttpMore = async (
  fileName: string,
  first: number,
  setData: (data: OptionMore[]) => void,
  setError: (error: string) => void,
  folder: string = ""
) => {
  const url = folder
    ? `/data/company/${folder}/${fileName}.json`
    : `/data/common/${fileName}.json`;

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to load data: ${response.statusText}`);

    const data: OptionMore[] = await response.json();
    if (!Array.isArray(data)) throw new Error(`Data is not an array`);

    const defaultOption = getDefaultMore(first);
    setData(defaultOption ? [defaultOption, ...data] : data);
  } catch (err) {
    handleError(err, setError);
  }
};

// Common error handler
const handleError = (err: unknown, setError: (error: string) => void) => {
  if (err instanceof Error) {
    console.error(`Error loading data:`, err.message);
    setError(err.message);
  } else {
    console.error(`Unexpected error:`, err);
    setError("An unexpected error occurred.");
  }
};

const parseFileList = async (
  fileListData: string | undefined,
  setFileList: React.Dispatch<React.SetStateAction<MediaList>>
): Promise<void> => {
  try {
    if(!fileListData) return;

    const parsedData = JSON.parse(fileListData);

    setFileList((prevFileList) => ({
      ...prevFileList,
      ...parsedData,
    }));
  } catch (error) {
    console.error("Error parsing file list:", error);
  }
};

const parseCommonDataList = async (
  dataList: string | undefined,
  setDataList: React.Dispatch<React.SetStateAction<CommonDataColumn>>
): Promise<void> => {
  if(!dataList) return;
  try {
    const parsedData = JSON.parse(dataList);

    await setDataList((prevFileList) => ({
      ...prevFileList,
      ...parsedData,
    }));
  } catch (error) {
    console.error("Error parsing file list:", error);
  }
};

const parseJsonData = async <T extends object>(
  fileListData: string | undefined,
  setFileList: React.Dispatch<React.SetStateAction<T>>
): Promise<void> => {
  try {
    if(!fileListData) return;

    const parsedData = JSON.parse(fileListData) as Partial<T>;

    setFileList((prevFileList) => ({
      ...prevFileList,
      ...parsedData,
    }));
  } catch (error) {
    console.error("Error parsing JSON data:", error);
  }
};

const parseJsonDataObject = async <T extends object | object[]>(
  fileListData: string | undefined,
  setFileList: React.Dispatch<React.SetStateAction<T>>
): Promise<void> => {
  try {
    if(!fileListData) return;

    const parsedData = JSON.parse(fileListData);

    setFileList((prevFileList: any) => {
      if (Array.isArray(parsedData)) {
        // Replace the whole array
        return parsedData as T;
      } else {
        // Merge objects
        return {
          ...prevFileList,
          ...parsedData,
        };
      }
    });
  } catch (error) {
    console.error("Error parsing JSON data:", error);
  }
};

const parseExamDataList = async (
  fileListData: string | undefined,
  setFileList: React.Dispatch<React.SetStateAction<CommonDataColumn>>
): Promise<void> => {
  try {
    if(!fileListData) return;
    
    const parsedData = JSON.parse(fileListData);

    setFileList((prevFileList) => ({
      ...prevFileList,
      ...parsedData,
    }));
  } catch (error) {
    console.error("Error parsing file list:", error);
  }
};

const extractCommonData = async (
  commonData: string | undefined,
): Promise<CommonDataColumn | null> => {
   try {
    if(commonData){
      const parsedData: CommonDataColumn = JSON.parse(commonData);
      return parsedData;
    }
    return null;
  } catch (error) {
    console.error("Error parsing file list:", error);
    return null;
  }
};

const parseFileListData = (dataString: string): MediaList | null => {
  try {
    // Parse the string into a JavaScript object
    const parsedData: Record<string, string> = JSON.parse(dataString);

    // Define the default structure with all properties as null
    const defaultFileList: MediaList = {
      profile: null,
      thumb: null,
      signature: null,
      logo: null,
      map: null,
      banner: null,
      document1: null,
      document2: null,
      document3: null,
      document4: null,
      document5: null,
      photo1: null,
      photo2: null,
      photo3: null,
      thumb1: null,
      thumb2: null,
      thumb3: null,
    };

    // Merge parsed data into the default structure
    return { ...defaultFileList, ...parsedData };
  } catch (error) {
    console.error("Error parsing file list data:", error);
    return null;
  }
};


const attachImage = async (
  file: File | null,
  size: Number | null,
  setError: (error: string) => void // Keep the type as is
): Promise<string | null> => {
  if (!file) {
    setError("No file selected.");
    return null;
  }

  await Utility.Animate.AttachFile.Start();

  const formData = new FormData();
  formData.append("file", file);
  
  try {
    const response = await apiFileUpload.post(`image/upload/${size?.toString()}`, formData);
    
    console.log(response.data);

    Utility.Animate.AttachFile.End();

    if (response.data?.fileName) {
      console.log(`File uploaded successfully: ${response.data.fileName}`);
      return response.data.fileName;
    } else {
      console.log("File uploaded, but no fileName returned.");
      return null;
    }
  } catch (error) {
    console.error("File upload error:", error);
    setError("Failed to upload file."); 
    return null;
  }
};

const attachLocal = async (
  file: File | null,
  setError: (error: string) => void // Keep the type as is
): Promise<string | undefined> => {
  if (!file) {
    setError("No file selected.");
    return undefined;
  }

  await Utility.Animate.AttachFile.Start();

  const formData = new FormData();
  formData.append("file", file);
  
  try {
    const response = await apiFileUpload.post(`local/upload/`, formData);
    
    console.log(response.data);

    Utility.Animate.AttachFile.End();

    if (response.data?.fileName) {
      console.log(`File uploaded successfully: ${response.data.fileName}`);
      return response.data.fileName;
    } else {
      console.log("File uploaded, but no fileName returned.");
      return undefined;
    }
  } catch (error) {
    console.error("File upload error:", error);
    setError("Failed to upload file."); 
    return undefined;
  }
};

const attachFile = async (
  file: File | null,
  folder: string,
  setError: (error: string) => void // Keep the type as is
): Promise<string | undefined> => {
  if (!file) {
    setError("No file selected.");
    return undefined;
  }

  console.log("file-upload---------> reached here")

  await Utility.Animate.AttachFile.Start();

  const formData = new FormData();
  formData.append("file", file);
  
  try {
    const companyId = Utility.Storage.CompanyId();
    const response = await apiFileUpload.post(`file/upload/${companyId}/${folder}/`, formData);
    
    console.log(response.data);

    Utility.Animate.AttachFile.End();

    if (response.data?.fileName) {
      let fileName = response.data?.fileName;
      fileName = fileName.split("/").pop();
      console.log(`File uploaded successfully(attachFile): ${fileName}`);
      return fileName;
    } else {
      console.log("File uploaded, but no fileName returned.");
      return undefined;
    }
  } catch (error) {
    console.error("File upload error:", error);
    setError("Failed to upload file."); 
    return undefined;
  }
};

const sendFileAsEmail = async (
  file: File | null,
  email: string,
  subject: string,
  content: string,
  setError: (error: string) => void
): Promise<string | undefined> => {
  if (!file) {
    setError("No file selected.");
    return undefined;
  }
  
  await Utility.Animate.AttachFile.Start();
  
  const formData = new FormData();
  formData.append("file", file);
  formData.append("email", email);
  formData.append("subject", subject);
  formData.append("content", content);
  
  try {
    const response = await apiFileUpload.post("file/send/", formData);
    
    console.log(response.data);
    Utility.Animate.AttachFile.End();
    
    if (response.data?.fileName) {
      let fileName = response.data?.fileName;
      fileName = fileName.split("/").pop();
      console.log(`File sent successfully`);
      return fileName;
    } else {
      console.log("Email sent, but no fileName returned.");
      return undefined;
    }
  } catch (error) {
    console.error("File sent error:", error);
    Utility.Animate.AttachFile.End(); // Add this to ensure animation ends on error
    setError("Failed to sent file."); 
    return undefined;
  }
};

const attachPhoto = async (
  file: File | null,
  folder: string,
  size: number | string,
  setError: (error: string) => void // Keep the type as is
): Promise<string | undefined> => {
  if (!file) {
    setError("No file selected.");
    return undefined;
  }

  await Utility.Animate.AttachFile.Start();

  const formData = new FormData();
  formData.append("file", file);
  
  try {
    const companyId = Utility.Storage.CompanyId();
    const response = await apiFileUpload.post(`file/upload/photo/${companyId}/${folder}/${size}/`, formData);
    
    console.log(response.data);

    Utility.Animate.AttachFile.End();



    if (response.data?.fileName) {
      let fileName = response.data?.fileName;
      fileName = fileName.split("/").pop();
      console.log(`File uploaded successfully(attachFile): ${fileName}`);
      return fileName;
    } else {
      console.log("File uploaded, but no fileName returned.");
      return undefined;
    }
  } catch (error) {
    console.error("File upload error:", error);
    setError("Failed to upload file."); 
    return undefined;
  }
};

const deleteImage = async (
  fileName: string | null,
  setFileList: React.Dispatch<React.SetStateAction<MediaList>>,
  setImageUrl: React.Dispatch<React.SetStateAction<string | null>>
) => {
  if (!fileName) {
    console.warn("No profile file to delete.");
    return;
  }

  await Utility.Animate.DeleteFile.Start();

  try {
    const response = await apiClient.delete(`image/delete/${fileName}`);

    if (response.status === 200) {
      console.log("Image deleted successfully");

      // Clear file list and image URL
      setFileList((prevFileList) => ({
        ...prevFileList,
        profile: "", // Ensure profile key is cleared
      }));
      setImageUrl(null);
    } else {
      console.error("Failed to delete image. Server response:", response);
    }
  } catch (error) {
    console.error("Error deleting image:", error);
  }

  Utility.Animate.DeleteFile.End();
};

const deletePhoto = async (
  fileName: string | null,
  folder: string | null,
  setFileList: React.Dispatch<React.SetStateAction<MediaList>>,
  setImageUrl: React.Dispatch<React.SetStateAction<string | null>>
) => {
  if (!fileName) {
    console.warn("No profile file to delete.");
    return;
  }

  await Utility.Animate.DeleteFile.Start();

  try {
    const companyId = Utility.Storage.CompanyId();
    const response = await apiClient.delete(`file/delete/${companyId}/${folder}/${fileName}`);

    if (response.status === 200) {
      console.log("Image deleted successfully");

      // Clear file list and image URL
      setFileList((prevFileList) => ({
        ...prevFileList,
        profile: "", // Ensure profile key is cleared
      }));
      setImageUrl(null);
    } else {
      console.error("Failed to delete image. Server response:", response);
    }
  } catch (error) {
    console.error("Error deleting image:", error);
  }

  Utility.Animate.DeleteFile.End();
};

const deleteLocal = async (
  fileName: string | null
) => {
  if (!fileName) {
    console.warn("No profile file to delete.");
    return;
  }

  await Utility.Animate.DeleteFile.Start();

  try {
    const response = await apiClient.delete(`local/delete/${fileName}`);

    if (response.status === 200) {
      console.log("File deleted successfully");
    } else {
      console.error("Failed to delete file. Server response:", response);
    }
  } catch (error) {
    console.error("Error deleting file:", error);
  }

  Utility.Animate.DeleteFile.End();
};

const deleteFile = async (
  fileName: string | null,
  folder: string
) => {
  if (!fileName) {
    console.warn("No profile file to delete.");
    return;
  }

  await Utility.Animate.DeleteFile.Start();

  try {
    const companyId = Utility.Storage.CompanyId();
    const response = await apiClient.delete(`file/delete/${companyId}/${folder}/${fileName}`);

    if (response.status === 200) {
      console.log("File deleted successfully");
    } else {
      console.error("Failed to delete file. Server response:", response);
    }
  } catch (error) {
    console.error("Error deleting file:", error);
  }

  Utility.Animate.DeleteFile.End();
};

const validateLocal = async (fileName: string | null): Promise<boolean> => {
  if (!fileName) {
    console.warn("No file name provided for validation.");
    return false;
  }

  try {
    const response = await apiClient.get(`local/validate/${fileName}`);

    if (response.data === true) {
      console.log("File exists:", fileName);
      return true;
    } else {
      console.warn("File does not exist:", fileName);
      return false;
    }
  } catch (error) {
    console.error("Error validating file:", error);
    return false;
  }
};

const validateFile = async (fileName: string | null, folder: string): Promise<boolean> => {
  if (!fileName) {
    console.warn("No file name provided for validation.");
    return false;
  }

  try {
    const companyId = Utility.Storage.CompanyId();
    const response = await apiClient.get(`file/validate/${companyId}/${folder}/${fileName}`);

    if (response.data === true) {
      console.log("File exists:", fileName);
      return true;
    } else {
      console.warn("File does not exist:", fileName);
      return false;
    }
  } catch (error) {
    console.error("Error validating file:", error);
    return false;
  }
};

const fetchImage = async (filename: string): Promise<string | null> => {
  try {
    console.error("fetchImage"); // Consider using console.log instead of console.error for non-error logs
    const response = await apiClient.get(`image/${filename}`, {
      responseType: 'arraybuffer', // Ensure the response is treated as binary data
    });

    const imageBlob = new Blob([response.data], { type: 'image/png' });
    const imageObjectUrl = URL.createObjectURL(imageBlob);
    //console.log("Image fetched successfully:", imageObjectUrl);
    return imageObjectUrl;
  } catch (error) {
    console.error("Error fetching image:", error);
    return null;
  }
};

const fetchPhoto = async (fileName: string, folder: string): Promise<string | null> => {
  try {
    console.error("fetchImage"); // Consider using console.log instead of console.error for non-error logs
    const companyId = Utility.Storage.CompanyId();
    const response = await apiClient.get(`file/view/${companyId}/${folder}/${fileName}`, {
      responseType: 'arraybuffer', // Ensure the response is treated as binary data
    });

    const imageBlob = new Blob([response.data], { type: 'image/png' });
    const imageObjectUrl = URL.createObjectURL(imageBlob);
    //console.log("Image fetched successfully:", imageObjectUrl);
    return imageObjectUrl;
  } catch (error) {
    console.error("Error fetching image:", error);
    return null;
  }
};

const fetchLocal = async (filename: string): Promise<string | null> => {
  try {
    console.log("Fetching file:", filename);

    const response = await apiClient.get(`local/${filename}`, {
      responseType: "arraybuffer", // Fetch as binary data
    });

    // Extract MIME type from Content-Type header
    const contentType = response.headers["content-type"] || "application/octet-stream";
    
    // Create a Blob for the file
    const fileBlob = new Blob([response.data], { type: contentType });

    // Generate a URL to download or display the file
    const fileObjectUrl = URL.createObjectURL(fileBlob);
    
    console.log("File fetched successfully:", fileObjectUrl);
    return fileObjectUrl;
  } catch (error) {
    console.error("Error fetching file:", error);
    return null;
  }
};

const fetchFile = async (filename: string, folder: string): Promise<string | null> => {
  try {
    console.log("Fetching file:", filename);

    const companyId = Utility.Storage.CompanyId();
    const response = await apiClient.get(`file/view/${companyId}/${folder}/${filename}`, {
      responseType: "arraybuffer", // Fetch as binary data
    });

    // Extract MIME type from Content-Type header
    const contentType = response.headers["content-type"] || "application/octet-stream";
    
    // Create a Blob for the file
    const fileBlob = new Blob([response.data], { type: contentType });

    // Generate a URL to download or display the file
    const fileObjectUrl = URL.createObjectURL(fileBlob);
    
    console.log("File fetched successfully:", fileObjectUrl);
    return fileObjectUrl;
  } catch (error) {
    console.error("Error fetching file:", error);
    return null;
  }
};

const fetchLocalContent = async (filename: string): Promise<string | null> => {
  try {
    console.log("Fetching file:", filename);

    const response = await apiClient.get(`local/${filename}`, {
      responseType: "arraybuffer", // Fetch as binary
    });

    const contentType = response.headers["content-type"] || "application/octet-stream";
    const fileBlob = new Blob([response.data], { type: contentType });

    // Read Blob content as text
    const fileContent = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsText(fileBlob);
    });

    console.log("File content fetched successfully");
    return fileContent;
  } catch (error) {
    console.error("Error fetching file:", error);
    return null;
  }
};

const fetchFileContent = async (filename: string, folder: string): Promise<string | null> => {
  try {
    console.log("Fetching file:", filename);

    const companyId = Utility.Storage.CompanyId();
    const response = await apiClient.get(`file/content/${companyId}/${folder}/${filename}`, {
      responseType: "arraybuffer", // Fetch as binary
    });

    const contentType = response.headers["content-type"] || "application/octet-stream";
    const fileBlob = new Blob([response.data], { type: contentType });

    // Read Blob content as text
    const fileContent = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsText(fileBlob);
    });

    console.log("File content fetched successfully");
    return fileContent;
  } catch (error) {
    console.error("Error fetching file:", error);
    return null;
  }
};

const loadImage = async (
  key: keyof MediaList, 
  ImageList: MediaList,
  fetchImage: (imageName: string) => Promise<string | null>,
  setUrl: React.Dispatch<React.SetStateAction<string | null>>
): Promise<boolean> => {
  const imageName = ImageList[key]; 
  if (imageName) {
    const url = await fetchImage(imageName);
    if (url) {
      setUrl(url);
      return true; // Image loaded successfully
    }
  }
  return false; // Image not loaded
};

const returnLoadImage = async (
  key: keyof MediaList, 
  ImageList: MediaList
): Promise<string | null> => {
  const imageName = ImageList[key]; 
  if (imageName) {
    const url = await Files.ImageAPI.Fetch(imageName);
    if (url) {
      console.log("returnLoadImage----->", url); 
      return url; 
    }
  }
  return null;  
};

const cleanFileList = (fileList: MediaList): MediaList => {
  return Object.fromEntries(
    Object.entries(fileList).filter(([key, value]) => value !== null)
  ) as MediaList;
};

// const cleanObject = <T extends Record<string, any>>(obj: T): T => {
//   return Object.fromEntries(
//     Object.entries(obj).filter(([_, value]) => value !== null)
//   ) as T;
// };


export const Files = {
  ImageAPI: {
    Attach: attachImage,
    Delete: deleteImage,
    Fetch: fetchImage,
    Load: loadImage,
    return: returnLoadImage,
  },
  Photo:{
    Attach: attachPhoto,
    Delete: deletePhoto,
    Fetch: fetchPhoto,
  },
  File: {
    Attach: attachFile,
    Delete: deleteFile,
    Fetch: fetchFile,
    Content: fetchFileContent,
    Exists: validateFile,
    Send: sendFileAsEmail,
  },
  Local: {
    Attach: attachLocal,
    Delete: deleteLocal,
    Fetch: fetchLocal,
    Content: fetchLocalContent,
    Exists: validateLocal,
  },
  List:{
    ParseFile: parseFileList,
    parseJsonData: parseJsonData,
    parseJsonDataObject: parseJsonDataObject,
    ParseCommonData: parseCommonDataList,
    ParseExamData: parseExamDataList,
    extractCommonData: extractCommonData,
    return: parseFileListData,
    Clean: cleanFileList,
  },
  Json:{
    API: loadFromAPI,
    APIMore: loadFromAPIMore,
    APIList: listFromAPI,
    Http: loadFromHttp,
    HttpMore: loadFromHttpMore,
    APILocal: loadFromAPISetLocalStorage,
    Any: loadAnyData,
    Fetch: fetchAnyData,
  }
};
